import type { ElementType } from "react";

import {
  PROJECTS_AREA_SEGMENT,
  PROJECTS_LIBRARY_SEGMENT,
  PROJECTS_TASKS_SEGMENT,
} from "@hightouch/lib/llm/asset-comments/paths";

import type { Crumb } from "src/components/layout/types";
import type { NavSectionLink } from "src/components/layout/nav/nav-section";
import {
  AMP_AD_CHATS_PATH,
  AMP_ADS_SECTION_PATH,
  AMP_CATALOG_ADS_PATH,
  AMP_NEW_AD_CHAT_PATH,
  AMP_INSIGHTS_PATH,
  AMP_LAUNCH_PATH,
  AMP_ROUTINES_PATH,
} from "src/components/layout/nav/sections/amp-nav-links";
import { AGENT_CHATS_PATH } from "src/components/layout/nav/sections/hooks/agents";
import { isContextPath } from "src/components/layout/nav/sections/hooks/projects";
import { isLinkActive } from "src/components/layout/nav/utils";
import { ACTIONS_PATHS } from "src/pages/agents/actions/constants";
import { ALL_INSIGHTS_NAV_ITEMS } from "src/pages/ad-studio/insights/insights-nav-items";
import {
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";
import {
  DRAFTS_VIEW,
  NEW_VIEW,
  LIBRARY_FOLDER_PARAM,
  LIBRARY_VIEWS,
  TYPE_LABELS,
} from "src/pages/projects/library/library-filters";
import { writeLibraryListFilters } from "src/pages/projects/library/use-library-list-filters";

export const ROOT_PANEL = "root";
export const PROJECTS_PANEL = "projects";
export const CHATS_PANEL = "chats";
// Kept as "library", the key viewers' saved nav layouts hide it by, though the nav calls it Content.
export const LIBRARY_PANEL = "library";
export const INSIGHTS_PANEL = "insights";
export const AUTOMATIONS_PANEL = "automations";

/** The pages the Automations panel lists. */
export const AUTOMATION_PATHS: readonly string[] = [
  AMP_ROUTINES_PATH,
  ACTIONS_PATHS.list,
  AMP_LAUNCH_PATH,
];

/** A CDP section: a panel of links, or one page the root links to directly. */
export type CdpEntry = {
  title: string;
  icon: ElementType;
  links: NavSectionLink[];
  isLink?: boolean;
};

const PROJECTS_PANEL_SEGMENTS = new Set([
  PROJECTS_AREA_SEGMENT,
  PROJECTS_TASKS_SEGMENT,
]);

/** A pathname's path below the workspace slug. */
export const workspacePath = (pathname: string) =>
  `/${pathname.split("/").slice(2).join("/")}`;

/** Whether a pathname, slug included, is the page at a root-relative path or under it. */
export const isUnder = (pathname: string, path: string) => {
  const rest = workspacePath(pathname);
  return rest === path || rest.startsWith(`${path}/`);
};

/** Whether a pathname, slug included, is one of Ad Studio's chats, its new chat, or its lists of chats, which Chats holds. */
export const isAdChatPath = (pathname: string) =>
  isUnder(pathname, AMP_NEW_AD_CHAT_PATH) ||
  isUnder(pathname, AMP_AD_CHATS_PATH);

/** Ad Studio's views of its ads, which Content lights its Ads row for. */
const AD_LIST_PATHS = ["ads", "favorites", "created-by-you", "folders"].map(
  (segment) => `${AMP_ADS_SECTION_PATH}/${segment}`,
);

/** The Library filtered to ads, which the Content panel lists as its Ads row. */
const CONTENT_ADS_HREF = `${PROJECTS_PATHS.library}?${writeLibraryListFilters(new URLSearchParams(), { fileTypes: ["ad"] })}`;

/** Whether a pathname, slug included, is one of Ad Studio's views of its ads. */
const isAdListPath = (pathname: string) =>
  AD_LIST_PATHS.some((path) => isUnder(pathname, path));

/** The panel a pathname, slug included, opens: the AMP panel its page is listed in, a CDP section's panel for its pages, else the root. */
export const panelForPath = (
  pathname: string,
  entries: CdpEntry[],
  /** Whether Chats is in the nav to hold Ad Studio's chats; without it they open the root. */
  hasChats = true,
): string => {
  const segment = pathname.split("/")[2] ?? "";
  if (isUnder(pathname, AGENT_CHATS_PATH)) {
    return CHATS_PANEL;
  }
  if (isAdChatPath(pathname)) {
    return hasChats ? CHATS_PANEL : ROOT_PANEL;
  }
  if (PROJECTS_PANEL_SEGMENTS.has(segment)) {
    return PROJECTS_PANEL;
  }
  if (
    segment === PROJECTS_LIBRARY_SEGMENT ||
    isUnder(pathname, AMP_CATALOG_ADS_PATH)
  ) {
    return LIBRARY_PANEL;
  }
  if (isUnder(pathname, AMP_INSIGHTS_PATH)) {
    return INSIGHTS_PANEL;
  }
  // Before Content, which would otherwise take Ad Studio's Launch page.
  if (AUTOMATION_PATHS.some((path) => isUnder(pathname, path))) {
    return AUTOMATIONS_PANEL;
  }
  // Ads are content, so every other Ad Studio page but those Context lists sits under Content.
  if (isUnder(pathname, AMP_ADS_SECTION_PATH) && !isContextPath(pathname)) {
    return LIBRARY_PANEL;
  }
  const section = entries.find(
    (entry) =>
      !entry.isLink &&
      entry.links.some((link) =>
        isLinkActive(pathname, link.href, link.isSub, link.activePaths),
      ),
  );
  return section?.title ?? ROOT_PANEL;
};

/** The pages under Insights by their segment, labelled as the Insights panel lists them. */
const INSIGHTS_PAGE_LABELS = new Map([
  ...ALL_INSIGHTS_NAV_ITEMS.map(({ path, label }) => [path, label] as const),
  ["settings", "Settings"],
]);

/** Ad Studio's new chat pages, where an ad starts. */
const NEW_AD_CHAT_PATHS = [AMP_NEW_AD_CHAT_PATH, `${AMP_NEW_AD_CHAT_PATH}/new`];

/** Whether a pathname, slug included, is one of Ad Studio's new chat pages, which no Chats row stands for. */
export const isNewAdChatPath = (pathname: string) =>
  NEW_AD_CHAT_PATHS.includes(workspacePath(pathname));

/**
 * The row an AMP panel lights for a pathname, slug included, as a crumb linked to that row.
 * A page no row stands for, such as a project or a Library folder, gets none.
 */
export const ampPanelItem = (
  panel: string,
  pathname: string,
  search: string,
): Crumb | undefined => {
  const path = workspacePath(pathname);
  switch (panel) {
    case CHATS_PANEL:
      // Chats here names the panel, so the new ad chat says what it starts.
      return isNewAdChatPath(pathname)
        ? { label: "New ad chat", link: AMP_NEW_AD_CHAT_PATH }
        : undefined;
    case PROJECTS_PANEL:
      if (path === PROJECTS_PATHS.projects) {
        return { label: "Overview", link: PROJECTS_PATHS.projects };
      }
      return isUnder(pathname, PROJECTS_PATHS.tasks)
        ? { label: TERMS.task.Plural, link: PROJECTS_PATHS.tasks }
        : undefined;
    case LIBRARY_PANEL: {
      if (isUnder(pathname, AMP_CATALOG_ADS_PATH)) {
        return { label: "Catalog ads", link: AMP_CATALOG_ADS_PATH };
      }
      if (isAdListPath(pathname)) {
        return { label: TYPE_LABELS.ad, link: CONTENT_ADS_HREF };
      }
      if (isUnder(pathname, PROJECTS_PATHS.libraryNew)) {
        return { label: NEW_VIEW.name, link: PROJECTS_PATHS.libraryNew };
      }
      if (path !== PROJECTS_PATHS.library) return undefined;
      const viewId = new URLSearchParams(search).get(LIBRARY_FOLDER_PARAM);
      if (!viewId) return { label: "Files", link: PROJECTS_PATHS.library };
      const view = [DRAFTS_VIEW, ...LIBRARY_VIEWS].find(
        ({ id }) => id === viewId,
      );
      return view
        ? {
            label: view.name,
            link: `${PROJECTS_PATHS.library}?${LIBRARY_FOLDER_PARAM}=${encodeURIComponent(view.id)}`,
          }
        : undefined;
    }
    case AUTOMATIONS_PANEL:
      if (isUnder(pathname, AMP_ROUTINES_PATH)) {
        return { label: "Routines", link: AMP_ROUTINES_PATH };
      }
      if (isUnder(pathname, AMP_LAUNCH_PATH)) {
        return { label: "Launch", link: AMP_LAUNCH_PATH };
      }
      return isUnder(pathname, ACTIONS_PATHS.list)
        ? { label: "Actions", link: ACTIONS_PATHS.list }
        : undefined;
    case INSIGHTS_PANEL: {
      const segment = path.slice(AMP_INSIGHTS_PATH.length + 1).split("/")[0];
      const label = INSIGHTS_PAGE_LABELS.get(segment ?? "");
      return label
        ? { label, link: `${AMP_INSIGHTS_PATH}/${segment}` }
        : undefined;
    }
    default:
      return undefined;
  }
};
